import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserAuth } from '../../hooks/useUserAuth';
import styles from './AuthGuard.module.scss';

const MAX_PARAM_LENGTH = 500;

const isValidParam = (value: string | null): boolean => {
  if (!value || typeof value !== 'string') return false;
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0 || trimmedValue.length > MAX_PARAM_LENGTH) return false;
  if (/<script|javascript:|data:|vbscript:/i.test(trimmedValue)) return false;
  return true;
};

const hasValidInvitationParams = (): boolean => {
  if (typeof window === 'undefined') return false;
  const searchParams = new URLSearchParams(window.location.search);
  const invitation = searchParams.get('invitation');
  const organization = searchParams.get('organization');

  return isValidParam(invitation) && isValidParam(organization);
};

export interface AuthGuardProps {
  children: React.ReactNode;
  loginPath?: string;
  loadingComponent?: React.ReactNode;
}

/**
 * Component to protect routes requiring authentication
 * Redirects to login page if user is not authenticated
 *
 * Usage:
 * ```typescript
 * <AuthGuard>
 *   <ProtectedContent />
 * </AuthGuard>
 * ```
 *
 * With custom login path:
 * ```typescript
 * <AuthGuard loginPath="/auth/login">
 *   <ProtectedContent />
 * </AuthGuard>
 * ```
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  loginPath = '/login',
  loadingComponent,
}) => {
  const { isAuthenticated, isLoading, error } = useAuth0();
  const { isLoading: isLoadingAuth } = useUserAuth();

  // Don't render anything on protected routes until Auth0 is initialized
  // This prevents premature redirects
  if (isLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }

    return <div className={styles.loadingContainer}>Loading...</div>;
  }

  // Handle Auth0 errors
  if (error) {
    console.error('Auth0 error in AuthGuard:', error);
    return (
      <div className={styles.errorContainer}>
        <h2>Authentication Error</h2>
        <p>{error.message}</p>
        <button onClick={() => (window.location.href = loginPath)} className={styles.errorButton}>
          Return to Login
        </button>
      </div>
    );
  }

  // Show loading while fetching tokens after authentication
  if (isAuthenticated && isLoadingAuth) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }

    return <div className={styles.loadingContainer}>Loading...</div>;
  }

  if (!isAuthenticated) {
    // On the server or in production, redirect immediately
    if (
      typeof window !== 'undefined' &&
      window.location.pathname !== loginPath &&
      !hasValidInvitationParams()
    ) {
      const returnTo = window.location.pathname + window.location.search;
      window.location.href = `${loginPath}?returnTo=${encodeURIComponent(returnTo)}`;
    }
    // Prevent rendering children while redirect is happening
    return null;
  }

  return <>{children}</>;
};
